import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Formik, Form, Field } from 'formik';
import {
  Alert,
  AlertDescription,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  GridItem,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Wrapper } from '@/components/login/Wrapper';
import { BsCheckCircleFill, BsDashCircleFill } from 'react-icons/bs';

import { extractErrors, flattenErrors, isInvalidField } from '@/lib/util';
import { renderErrors } from '@/ui/util';
import DividerBox from '@/ui/DividerBox';
import { PasswordHelper } from '@/ui/PasswordStrengthMeter';
import GoogleIcon from '@/icons/Google';

import { useAuth, useQueryParams, useRedirectData } from '@/hooks';
import { newSignup, createSignup } from '@/store/userRegistrations/actions';

import { useSchema } from './schema';
import { Microsoft } from '@/assets/icons/mailbox';
import { findSuggestedTimezone } from '@/lib/date';
import useUrlState from '@ahooksjs/use-url-state';
import { KFormLabel } from '@/ui-library/components';

export const Signup = () => {
  const { current } = useRef({});
  const redirectData = useRedirectData();
  const dispatch = useDispatch();
  const history = useHistory();
  const { setAuth, bootstrap } = useAuth();

  const [data, setData] = useState({});
  const [errors, setErrors] = useState([]);
  const [showHelper, setShowHelper] = useState(false);
  const [disallowSignup, setDisallowSignup] = useState(true);
  const params = useQueryParams();
  const [state] = useUrlState(params);
  const { redirect: redirectQueryParam } = state;
  const inputStyles = {
    _active: { borderColor: 'secondary-700' },
    _hover: { borderColor: 'secondary-700' },
    _focus: { borderColor: 'secondary-700' },
  };

  const validationSchema = useSchema(current, !disallowSignup);

  useEffect(() => {
    function addErrors(err) {
      if (err) {
        err = errors.concat(flattenErrors(err));
        if (err.length) {
          setErrors(err);
          return true;
        }
      }
    }
    // handle OAuth redirect, if present
    if (redirectData) {
      const { data, errors, status } = redirectData;
      addErrors(errors);
      if (data?.access_token) {
        // sign-up and sign-in was successful
        // `app/index.jsx` would have handled `setAuth`
        // just `bootstrap` and navigate to home
        Promise.resolve()
          .then(bootstrap)
          .then(() => {
            if (data?.redirect?.length) {
              window.open(
                `${window.location.origin + data?.redirect}`,
                '_self'
              );
            } else {
              window.open(`${window.location.origin + '/dashboard'}`, '_self');
            }
          });
        return;
      }
      if (status && status < 400) {
        // sign-up successful, but unable to auto sign-in (perhaps account is pending confirmation?)
        // nothing else to do, just navigate to login
        Promise.resolve().then(() => history.push('/login'));
        return;
      }
    }
    // fetch invite to populate sign-up form
    dispatch(newSignup({ search: window.location.search }))
      .unwrap()
      .then(({ data, errors }) => {
        if (!addErrors(errors)) setDisallowSignup(false);
        if (data?.user) data.user.time_zone = findSuggestedTimezone();
        if (Object.keys(data).length) setData(data);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = useCallback(
    async (values, actions) => {
      try {
        const { first_name, last_name, password, time_zone } = values;
        const user = { first_name, last_name, password, time_zone };
        const { data, errors, status } = await dispatch(
          createSignup({ search: window.location.search, user })
        ).unwrap();
        // check errors
        if (status && status >= 400) {
          extractErrors(errors, 'user', actions.setFieldError);
        } else {
          // sign-up successful
          if (data?.access_token) {
            // and sign-in was also successful
            setAuth(data.access_token);
            bootstrap();
          }
          // navigate to home
          Promise.resolve().then(() => {
            // history.push('/dashboard');
            if (redirectQueryParam?.length) {
              window.open(
                `${window.location.origin + redirectQueryParam}`,
                '_self'
              );
            } else {
              window.open(`${window.location.origin + '/dashboard'}`, '_self');
            }
          });
        }
      } catch (e) {
        console.error(e);
        setErrors(['System error']);
      } finally {
        actions.setSubmitting(false);
      }
    },
    [setAuth, bootstrap, dispatch, redirectQueryParam]
  );

  const { tFeGoogleIncrementalScopes } = useFlags();

  const googleOauthParams = new URLSearchParams({
    for: 'invite',
    invite: data.invitation?.token || '',
    origin: window.location.href,
    time_zone: data.user?.time_zone || '',
    redirect: redirectQueryParam || '',
  });

  const msOauthParams = new URLSearchParams({
    for: 'invite',
    invite: data.invitation?.token || '',
    origin: window.location.href,
    time_zone: data.user?.time_zone || '',
    redirect: redirectQueryParam || '',
  });

  if (!tFeGoogleIncrementalScopes) {
    googleOauthParams.delete('for'); // revert to old behaviour
  }

  if (!tFeGoogleIncrementalScopes) {
    msOauthParams.delete('for'); // revert to old behaviour
  }

  return (
    <Wrapper
      title="Recruitment happens here"
      desc="A recruitment platform. For recruiters. By recruiters."
      isSignUp={true}
    >
      <Box maxHeight="100%">
        <Stack>
          {data?.invitation && (
            <Stack align="center" my="4px">
              <Heading fontSize="24px" color="primary-900">
                You have been invited
              </Heading>
              <Text color="primary-900">
                <Text as="b">
                  {data?.invitation?.invited_by?.name || 'Kula'}
                </Text>{' '}
                has invited you to join as a member.
              </Text>
            </Stack>
          )}
          <Stack mt="4px">
            {errors.map((error, idx) => (
              <Alert
                key={idx}
                status="error"
                bg="#FDEDEF"
                color="#EB4A27"
                fontSize="14px"
                fontWeight="semiBold"
                borderRadius="8px"
              >
                <AlertDescription
                  lineHeight="1.5"
                  data-test-id="sign-up-alert-description"
                >
                  {error}
                </AlertDescription>
              </Alert>
            ))}
            <form
              method="post"
              action={`/api/v1/auth/google_oauth2?${googleOauthParams}`}
            >
              <input
                type="hidden"
                name="authenticity_token"
                value={data.csrf_token || ''}
              />
              <input
                type="hidden"
                name="login_hint"
                value={data.user?.email || ''}
              />
              <Button
                type="submit"
                variant="quaternary"
                width="full"
                height="40px"
                leftIcon={<GoogleIcon />}
                disabled={disallowSignup}
                data-test-id="sign-up-via-invite-microsoft"
              >
                Sign up with Google
              </Button>
            </form>
            <form
              method="post"
              action={`/api/v1/auth/microsoft_graph?${msOauthParams}`}
            >
              <input
                type="hidden"
                name="authenticity_token"
                value={data.csrf_token || ''}
              />
              <input
                type="hidden"
                name="login_hint"
                value={data.user?.email || ''}
              />
              <Button
                type="submit"
                variant="quaternary"
                width="full"
                height="40px"
                leftIcon={<Microsoft />}
                disabled={disallowSignup}
              >
                Sign up with Microsoft
              </Button>
            </form>
            <DividerBox>
              <Text>Or, sign up with your email</Text>
            </DividerBox>
            <Formik
              enableReinitialize
              initialValues={data.user}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {(formik) => (
                <Form>
                  <Stack spacing={2}>
                    <SimpleGrid columns={2} columnGap={4}>
                      <GridItem colSpan={1}>
                        <Field name="first_name">
                          {({ field, form, meta }) => (
                            <FormControl isInvalid={isInvalidField(form, meta)}>
                              <KFormLabel
                                htmlFor={field.name}
                                color="secondary-900"
                              >
                                First name
                              </KFormLabel>
                              <Input
                                {...field}
                                id={field.name}
                                value={field.value || ''}
                                disabled={disallowSignup}
                                {...inputStyles}
                              />
                              <FormErrorMessage
                                flexDir="column"
                                alignItems="flex-start"
                              >
                                {renderErrors('First Name', meta.error)}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Field name="last_name">
                          {({ field, form, meta }) => (
                            <FormControl isInvalid={isInvalidField(form, meta)}>
                              <KFormLabel
                                htmlFor={field.name}
                                color="secondary-900"
                              >
                                Last name
                              </KFormLabel>
                              <Input
                                {...field}
                                id={field.name}
                                value={field.value || ''}
                                disabled={disallowSignup}
                                {...inputStyles}
                              />
                              <FormErrorMessage
                                flexDir="column"
                                alignItems="flex-start"
                              >
                                {renderErrors('Last Name', meta.error)}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </GridItem>
                    </SimpleGrid>
                    <Field name="email">
                      {({ field, form, meta }) => (
                        <FormControl isInvalid={isInvalidField(form, meta)}>
                          <KFormLabel
                            htmlFor={field.name}
                            color="secondary-900"
                          >
                            Email address
                          </KFormLabel>
                          <Input
                            {...field}
                            id={field.name}
                            type="email"
                            value={field.value || ''}
                            disabled
                            {...inputStyles}
                          />
                          <FormErrorMessage
                            flexDir="column"
                            alignItems="flex-start"
                          >
                            {renderErrors('Email Address', meta.error)}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    <Field name="password">
                      {({ field, form, meta }) => (
                        <FormControl isInvalid={isInvalidField(form, meta)}>
                          <KFormLabel
                            htmlFor={field.name}
                            color="secondary-900"
                          >
                            Password
                          </KFormLabel>
                          <InputGroup>
                            <Input
                              {...field}
                              id={field.name}
                              value={field.value || ''}
                              type="password"
                              disabled={disallowSignup}
                              onFocus={() => setShowHelper(true)}
                              onBlur={(e) => {
                                field.onBlur(e);
                                if (!meta.error) {
                                  setShowHelper(false);
                                }
                              }}
                              {...inputStyles}
                            />
                            <InputRightElement
                              onClick={() => {
                                setShowHelper(false);
                              }}
                            >
                              {!meta.touched ? null : (
                                <Icon
                                  as={
                                    meta.error
                                      ? BsDashCircleFill
                                      : BsCheckCircleFill
                                  }
                                  color={meta.error ? 'red.500' : 'green.500'}
                                  boxSize="20px"
                                />
                              )}
                            </InputRightElement>
                          </InputGroup>
                          <FormErrorMessage
                            flexDir="column"
                            alignItems="flex-start"
                            w="calc(100% - 128px)"
                          >
                            {meta.error === 'password-complexity'
                              ? ''
                              : renderErrors('Password', meta.error)}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    {showHelper && (
                      <Box position="relative" w="100%">
                        <PasswordHelper
                          position="absolute"
                          top={{ base: '0', md: '50%' }}
                          right={{ base: '0', md: '-180px' }}
                          transform={{ md: 'translateY(-50%)' }}
                          maxW={{ base: '100%', md: '260px' }}
                          zIndex="10"
                          value={formik?.values?.password}
                        />
                      </Box>
                    )}

                    <Field name="password_confirmation">
                      {({ field, form, meta }) => (
                        <FormControl isInvalid={isInvalidField(form, meta)}>
                          <KFormLabel
                            htmlFor={field.name}
                            color="secondary-900"
                          >
                            Password confirmation
                          </KFormLabel>
                          <Input
                            {...field}
                            id={field.name}
                            type="password"
                            value={field.value || ''}
                            disabled={disallowSignup}
                            {...inputStyles}
                          />
                          <FormErrorMessage
                            flexDir="column"
                            alignItems="flex-start"
                          >
                            {renderErrors('', meta.error)}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Stack>
                  <Stack py="12px">
                    <Button
                      type="submit"
                      disabled={disallowSignup}
                      isLoading={formik.isSubmitting}
                      height="40px"
                      data-test-id="sign-up-button"
                    >
                      Sign up
                    </Button>
                  </Stack>
                </Form>
              )}
            </Formik>
          </Stack>
        </Stack>
      </Box>
    </Wrapper>
  );
};
